import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw, Sparkles } from "lucide-react";
import type { ContentMode, DraftStatus } from "@app/shared";
import { api, type DraftFilters } from "../lib/api";
import { useActiveBrand } from "../lib/brand";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { DraftCard } from "../components/DraftCard";
import {
  ImageSourceSelect,
  useImageSourceState,
} from "../components/ImageSourceSelect";
import { useToast } from "../components/Toast";

const STATUS_OPTIONS: { value: "" | DraftStatus; label: string }[] = [
  { value: "", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "approved", label: "Approved" },
  { value: "scheduled", label: "Scheduled" },
  { value: "posted", label: "Posted" },
  { value: "failed", label: "Failed" },
];

/**
 * Convert a `YYYY-MM-DD` date-input value into a Date at the end of that *local*
 * day (23:59:59.999). Parsing the parts manually avoids `new Date("YYYY-MM-DD")`
 * being interpreted as UTC midnight, which would otherwise exclude the day.
 */
function toEndOfLocalDay(ymd: string): Date {
  const [year, month, day] = ymd.split("-").map(Number);
  const end = new Date(year, month - 1, day);
  end.setHours(23, 59, 59, 999);
  return end;
}

/** Local "YYYY-MM-DD" for a Date. */
function toLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Inclusive day count between two YYYY-MM-DD strings (0 if invalid/reversed). */
function daysInclusive(from: string, to: string): number {
  const a = Date.parse(from);
  const b = Date.parse(to);
  if (Number.isNaN(a) || Number.isNaN(b) || a > b) return 0;
  return Math.round((b - a) / 86_400_000) + 1;
}

/** Default generate target — today + 7 (rolling D+7). */
const DEFAULT_GEN_DATE = toLocalYmd(new Date(Date.now() + 7 * 86_400_000));

interface DraftsListProps {
  /**
   * Restrict the list to a single content mode. `/drafts` passes `"reguler"`;
   * the Special Case page embeds this with `"special"` to show its produced
   * drafts. When set, the per-slot Generate action is shown only for `reguler`
   * (Special drafts are generated per-highlight; Article drafts elsewhere).
   */
  mode?: ContentMode;
}

/** Drafts overview with status + date-range filtering and a generate action. */
export function DraftsList({ mode }: DraftsListProps = {}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const accountKey = useActiveBrand();

  const [status, setStatus] = useState<"" | DraftStatus>("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const filters: DraftFilters = useMemo(
    () => ({
      // Scope the list to the active brand. Without this an admin (who can see
      // all brands) gets every brand's drafts mixed together, since the API
      // applies no brand filter when accountKey is omitted. Mirrors the other
      // workspace pages (Media / Materi / Articles / SpecialCase).
      accountKey: accountKey ?? undefined,
      mode,
      status: status || undefined,
      from: from ? new Date(from).toISOString() : undefined,
      // The API applies `targetPublishAt <= to`; without this, a bare date
      // parses to UTC midnight and excludes the selected day. Push "To" to the
      // end of the selected local day so it's inclusive of the whole day.
      to: to ? toEndOfLocalDay(to).toISOString() : undefined,
    }),
    [accountKey, mode, status, from, to],
  );

  const draftsQuery = useQuery({
    queryKey: ["drafts", filters],
    queryFn: ({ signal }) => api.drafts(filters, signal),
    enabled: !!accountKey,
  });

  const showGenerate = mode === undefined || mode === "reguler";
  const [genFrom, setGenFrom] = useState(DEFAULT_GEN_DATE);
  const [genTo, setGenTo] = useState(DEFAULT_GEN_DATE);
  const [imageSource, setImageSource] = useImageSourceState();
  const genCount = daysInclusive(genFrom, genTo);

  const generate = useMutation({
    mutationFn: () => {
      if (!accountKey) throw new Error("Pilih brand terlebih dahulu");
      return api.generateDraft({
        accountKey,
        from: genFrom,
        to: genTo,
        imageSource,
      });
    },
    onSuccess: (res) => {
      toast.success(
        `${res.count} draft diminta — akan muncul bertahap (±30 dtk per draft).`,
      );
      // Drafts are produced asynchronously by the worker; refetch a few times.
      [8000, 20000, 40000].forEach((ms) =>
        setTimeout(
          () => void queryClient.invalidateQueries({ queryKey: ["drafts"] }),
          ms,
        ),
      );
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Generation failed"),
  });

  const drafts = draftsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-navy">
            {mode === "special" ? "Special Case drafts" : "Drafts"}
          </h1>
          <p className="text-sm text-slate-500">
            Review, edit, and schedule generated content.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => draftsQuery.refetch()}
            disabled={draftsQuery.isFetching}
          >
            <RefreshCw
              className={
                "h-4 w-4" + (draftsQuery.isFetching ? " animate-spin" : "")
              }
            />
            Refresh
          </Button>
        </div>
      </div>

      {showGenerate ? (
        <Card>
          <CardContent className="flex flex-wrap items-end gap-4 p-4">
            <div className="space-y-1.5">
              <Label htmlFor="gen-from">Generate dari tanggal</Label>
              <Input
                id="gen-from"
                type="date"
                value={genFrom}
                onChange={(e) => setGenFrom(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gen-to">sampai</Label>
              <Input
                id="gen-to"
                type="date"
                value={genTo}
                onChange={(e) => setGenTo(e.target.value)}
              />
            </div>
            <div className="w-44">
              <ImageSourceSelect
                id="gen-image-source"
                value={imageSource}
                onChange={setImageSource}
                disabled={generate.isPending}
              />
            </div>
            <p className="min-w-[12rem] flex-1 text-xs text-slate-500">
              {genCount > 31
                ? "Rentang maksimal 31 hari."
                : genCount > 0
                  ? `Membuat ${genCount} draft (1 per hari). Proses berjalan otomatis di latar belakang.`
                  : "Tanggal tidak valid — pastikan ‘dari’ ≤ ‘sampai’."}
            </p>
            <Button
              variant="primary"
              onClick={() => generate.mutate()}
              disabled={
                !accountKey ||
                generate.isPending ||
                genCount < 1 ||
                genCount > 31
              }
            >
              {generate.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Generate{genCount > 1 ? ` ${genCount} hari` : ""}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="status">Status</Label>
            <select
              id="status"
              value={status}
              onChange={(e) => setStatus(e.target.value as "" | DraftStatus)}
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-1"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="from">From (target publish)</Label>
            <Input
              id="from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="to">To (target publish)</Label>
            <Input
              id="to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {draftsQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-teal" />
        </div>
      ) : draftsQuery.isError ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-coral">
            Failed to load drafts.
          </CardContent>
        </Card>
      ) : drafts.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-sm text-slate-500">
            {mode === "special"
              ? "No Special Case drafts yet. Generate one from a highlight above."
              : "No drafts match these filters. Use Generate to create the next D+7 slot."}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {drafts.map((draft) => (
            <DraftCard key={draft.id} draft={draft} />
          ))}
        </div>
      )}
    </div>
  );
}
